import React, { useEffect, useMemo, useRef, useState } from "react";
import { Platform, Text, TouchableOpacity, View } from "react-native";

import { FASTAPI_BASE_URL, JOURNEY_ASSET_BASE_URL } from "../../config";

const getJourneyAssetUrl = (assetPath) => {
  const cleanPath = String(assetPath || "").replace(/^\/+/, "");
  return cleanPath ? `${JOURNEY_ASSET_BASE_URL}/${cleanPath}` : "";
};

function ReadingItem({ item, palette }) {
  if (!item || typeof item !== "object") return null;

  if (item.type === "paragraph") {
    return (
      <Text
        style={{
          color: palette.text,
          fontSize: 15,
          lineHeight: 24,
          marginBottom: 14,
        }}
      >
        {item.text}
      </Text>
    );
  }

  if (item.type === "callout") {
    return (
      <View
        style={{
          backgroundColor: palette.calloutSoft,
          borderColor: palette.calloutBorder,
          borderLeftWidth: 4,
          borderRadius: 10,
          borderWidth: 1,
          marginBottom: 16,
          padding: 16,
        }}
      >
        {item.title ? (
          <Text
            style={{
              color: palette.calloutAccent,
              fontSize: 13,
              fontWeight: "800",
              letterSpacing: 0.5,
              marginBottom: 8,
              textTransform: "uppercase",
            }}
          >
            {item.title}
          </Text>
        ) : null}
        <Text
          style={{
            color: palette.text,
            fontSize: 15,
            fontWeight: "600",
            lineHeight: 24,
          }}
        >
          {item.text}
        </Text>
      </View>
    );
  }

  if (item.type === "numbered_list" || item.type === "bullet_list") {
    const items = Array.isArray(item.items) ? item.items : [];
    return (
      <View
        style={{
          backgroundColor: palette.listSoft,
          borderColor: palette.border,
          borderRadius: 12,
          borderWidth: 1,
          marginBottom: 16,
          padding: 16,
        }}
      >
        {item.title ? (
          <Text
            style={{
              color: palette.accent,
              fontSize: 14,
              fontWeight: "900",
              letterSpacing: 0.6,
              marginBottom: 10,
            }}
          >
            {item.title}
          </Text>
        ) : null}
        {items.map((entry, index) => (
          <View
            key={`${item.title || item.type}-${index}`}
            style={{ flexDirection: "row", marginBottom: 8 }}
          >
            <Text
              style={{
                color: palette.accent,
                fontSize: 15,
                fontWeight: "900",
                lineHeight: 23,
                marginRight: 9,
                minWidth: 20,
              }}
            >
              {item.type === "numbered_list" ? `${index + 1}.` : "•"}
            </Text>
            <Text
              style={{
                color: palette.text,
                flex: 1,
                fontSize: 15,
                fontWeight: item.emphasize ? "700" : "500",
                lineHeight: 23,
              }}
            >
              {entry}
            </Text>
          </View>
        ))}
      </View>
    );
  }

  if (item.type === "question") {
    return (
      <View
        style={{
          backgroundColor: palette.questionSoft,
          borderColor: palette.questionBorder,
          borderRadius: 12,
          borderWidth: 1,
          marginTop: 4,
          padding: 17,
        }}
      >
        <Text
          style={{
            color: palette.questionAccent,
            fontSize: 16,
            fontWeight: "900",
            lineHeight: 25,
          }}
        >
          {item.text}
        </Text>
      </View>
    );
  }

  return null;
}

export default function JourneyStoryPrelude({
  block,
  c,
  t = (value) => value,
  authToken = null,
  materials = [],
  guidedPrelude = false,
}) {
  const [activeTab, setActiveTab] = useState("watch");
  // The self-guided WonderLabs renderer opts in explicitly.
  // Legacy and teacher-led uses keep their existing tabbed presentation.
  const videoRef = useRef(null);
  const [playbackNotice, setPlaybackNotice] = useState("");
  const [storyStarted, setStoryStarted] = useState(false);
  const [storyFinished, setStoryFinished] = useState(false);
  const [captionTrackUrl, setCaptionTrackUrl] = useState(null);
  const watchStory = () => {
    // Keep video mounted in both tabs: .play() remains inside this click event,
    // preserving browser user activation even when switching from Read.
    setActiveTab("watch");
    if (Platform.OS !== "web" || !videoRef.current) return;
    setPlaybackNotice("");
    const started = videoRef.current.play?.();
    if (started && typeof started.catch === "function") {
      started.catch(() => setPlaybackNotice(
        "Press Play in the video to begin the story."
      ));
    }
  };

  const palette = useMemo(
    () => ({
      accent: c?.accent || "#087caf",
      accentSoft: c?.accentSoft || "#eaf7fd",
      border: c?.border || "#d5e1ec",
      calloutAccent: "#6d28d9",
      calloutBorder: "#c4b5fd",
      calloutSoft: "#f5f3ff",
      card: c?.card || c?.surface || "#ffffff",
      listSoft: c?.surfaceSoft || "#f8fafc",
      muted: c?.mutedText || c?.textMuted || "#64748b",
      questionAccent: "#9a3412",
      questionBorder: "#fdba74",
      questionSoft: "#fff7ed",
      text: c?.text || "#111827",
    }),
    [c]
  );

  const videoUrl = getJourneyAssetUrl(block?.video?.path);
  const posterUrl = getJourneyAssetUrl(block?.video?.poster);
  const assetPath = block?.video?.path;
  // Preludes are authored local MP4s (not Saras source-video IDs). Fetch the
  // same private, path-keyed WebVTT used by the local science video blocks.
  useEffect(() => {
    if (Platform.OS !== "web" || !authToken || !assetPath) {
      setCaptionTrackUrl(null);
      return undefined;
    }
    let disposed = false;
    let blobUrl = null;
    const controller = new AbortController();
    setCaptionTrackUrl(null);
    const url = `${FASTAPI_BASE_URL}/wonderlabs/local-video-captions` +
      `?asset_path=${encodeURIComponent(assetPath)}&language_id=1`;
    fetch(url, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${authToken}` },
    }).then(async (response) => {
      if (response.status === 204) return null;
      if (!response.ok) throw new Error(`Prelude captions unavailable: ${response.status}`);
      return response.text();
    }).then((vtt) => {
      if (disposed || !vtt?.startsWith("WEBVTT")) return;
      blobUrl = URL.createObjectURL(new Blob([vtt], { type: "text/vtt" }));
      setCaptionTrackUrl(blobUrl);
    }).catch((error) => {
      if (!disposed && error?.name !== "AbortError") {
        console.warn("[WonderLabs captions] Prelude unavailable:", error?.message);
      }
    });
    return () => {
      disposed = true;
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [authToken, assetPath]);
  const readingContent = Array.isArray(block?.reading?.content)
    ? block.reading.content
    : [];

  const tabStyle = (tabName) => {
    const active = activeTab === tabName;
    return {
      alignItems: "center",
      backgroundColor: active ? palette.accent : palette.card,
      borderColor: active ? palette.accent : palette.border,
      borderRadius: 10,
      borderWidth: 1,
      flex: 1,
      justifyContent: "center",
      minHeight: 44,
      minWidth: 190,
      paddingHorizontal: 14,
      paddingVertical: 10,
    };
  };

  const tabTextStyle = (tabName) => ({
    color: activeTab === tabName ? "#ffffff" : palette.text,
    fontSize: 14,
    fontWeight: "800",
  });

  return (
    <View
      style={{
        backgroundColor: palette.card,
        borderColor: palette.border,
        borderRadius: 16,
        borderWidth: 1,
        marginBottom: 20,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          backgroundColor: palette.accentSoft,
          borderBottomColor: palette.border,
          borderBottomWidth: 1,
          paddingHorizontal: 20,
          paddingVertical: 18,
        }}
      >
        <Text
          style={{
            color: palette.accent,
            fontSize: 12,
            fontWeight: "900",
            letterSpacing: 0.8,
            marginBottom: 6,
            textTransform: "uppercase",
          }}
        >
          {block?.eyebrow || t("Story Prelude")}
        </Text>
        <Text
          style={{
            color: palette.text,
            fontSize: 22,
            fontWeight: "900",
            lineHeight: 28,
            marginBottom: block?.prompt ? 6 : 0,
          }}
        >
          {block?.title || t("Enter the Story")}
        </Text>
        {block?.prompt ? (
          <Text
            style={{
              color: palette.muted,
              fontSize: 14,
              lineHeight: 21,
            }}
          >
            {block.prompt}
          </Text>
        ) : null}
      </View>

      <View style={{ padding: 16 }}>
        {!guidedPrelude ? <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityState={{ selected: activeTab === "watch" }}
            onPress={watchStory}
            style={[tabStyle("watch"), { marginRight: 10, marginBottom: 8 }]}
          >
            <Text style={tabTextStyle("watch")}>🎬 {t("Watch the Story")}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            accessibilityRole="button"
            accessibilityState={{ selected: activeTab === "read" }}
            onPress={() => {
              videoRef.current?.pause?.();
              setActiveTab("read");
            }}
            style={[tabStyle("read"), { marginBottom: 8 }]}
          >
            <Text style={tabTextStyle("read")}>📖 {block?.reading?.tab_label || t("Read the Emergency Report")}</Text>
          </TouchableOpacity>
        </View> : null}

        {guidedPrelude ? (
          <Text style={{ color: palette.muted, marginBottom: 10, fontWeight: "700" }}>
            {activeTab === "watch" ? t("First, discover what happened.")
              : activeTab === "read" ? t("Now investigate the mission briefing and its constraints.")
              : t("Get your experiment materials ready before building.")}
          </Text>
        ) : null}

        {block?.self_guided_active && block?.master_teacher_watch && activeTab === "watch" && !storyStarted ? (
          <TouchableOpacity accessibilityRole="button" onPress={watchStory}
            style={{ backgroundColor: palette.accent, alignItems: "center",
              paddingVertical: 18, paddingHorizontal: 16, marginBottom: 12, borderRadius: 12 }}>
            <Text style={{ fontSize: 20, fontWeight: "900", color: "#fff", textAlign: "center" }}>
              {`▶ ${block.master_teacher_watch.button || "Watch the story"}`}
            </Text>
            <Text style={{ color: "#fff", fontSize: 15, marginTop: 6, textAlign: "center" }}>
              {block.master_teacher_watch.why || "Watch for clues to the mystery."}
            </Text>
          </TouchableOpacity>
        ) : null}
        {block?.self_guided_active && block?.master_teacher_watch && storyFinished && activeTab === "watch" ? (
          <Text style={{ fontSize: 16, fontWeight: "800", color: palette.text,
            marginBottom: 10 }}>{block.master_teacher_watch.after ||
              "Now it's your turn to investigate."}</Text>
        ) : null}
        {/* Keep the video in the document when reading; it can play on the
            same click that brings the Watch tab back into view. */}
        <View style={{ display: activeTab === "watch" ? "flex" : "none" }}>
            {Platform.OS === "web" && videoUrl ? (
              <video
                ref={videoRef}
                onPlay={() => setStoryStarted(true)}
                onEnded={() => setStoryFinished(true)}
                aria-label={block?.video?.title || block?.title || t("Journey story video")}
                controls
                playsInline
                poster={posterUrl || undefined}
                preload="metadata"
                src={videoUrl}
                style={{
                  backgroundColor: "#000000",
                  borderRadius: 12,
                  display: "block",
                  height: "auto",
                  maxHeight: 560,
                  width: "100%",
                }}
              >
                {captionTrackUrl && (
                  <track kind="captions" src={captionTrackUrl}
                    srcLang="en" label="English" default />
                )}
              </video>
            ) : (
              <View
                style={{
                  alignItems: "center",
                  backgroundColor: "#111827",
                  borderRadius: 12,
                  justifyContent: "center",
                  minHeight: 220,
                  padding: 24,
                }}
              >
                <Text
                  style={{
                    color: "#ffffff",
                    fontSize: 15,
                    lineHeight: 22,
                    textAlign: "center",
                  }}
                >
                  {t("This rapid-deployment story video is currently available in the web app.")}
                </Text>
              </View>
            )}

            {playbackNotice ? (
              <Text accessibilityRole="alert" style={{ color: palette.muted, marginTop: 8 }}>
                {t(playbackNotice)}
              </Text>
            ) : null}
            {block?.video?.caption ? (
              <Text
                style={{
                  color: palette.muted,
                  fontSize: 13,
                  lineHeight: 19,
                  marginTop: 10,
                }}
              >
                {block.video.caption}
              </Text>
            ) : null}
          </View>
        {activeTab === "read" ? (
          <View
            style={{
              backgroundColor: palette.card,
              borderColor: palette.border,
              borderRadius: 12,
              borderWidth: 1,
              padding: 20,
            }}
          >
            {block?.reading?.eyebrow ? (
              <Text
                style={{
                  color: palette.accent,
                  fontSize: 12,
                  fontWeight: "900",
                  letterSpacing: 0.7,
                  marginBottom: 6,
                  textTransform: "uppercase",
                }}
              >
                {block.reading.eyebrow}
              </Text>
            ) : null}

            {block?.reading?.title ? (
              <Text
                style={{
                  color: palette.text,
                  fontSize: 22,
                  fontWeight: "900",
                  lineHeight: 29,
                  marginBottom: 16,
                }}
              >
                {block.reading.title}
              </Text>
            ) : null}

            {readingContent.map((item, index) => (
              <ReadingItem
                key={`${block?.id || "story-prelude"}-reading-${index}`}
                item={item}
                palette={palette}
              />
            ))}
          </View>
        ) : null}
        {guidedPrelude && activeTab === "ready" ? (
          <View style={{ borderWidth: 1, borderColor: palette.border,
            borderRadius: 12, padding: 18, backgroundColor: palette.listSoft }}>
            <Text style={{ fontSize: 20, fontWeight: "900", color: palette.text }}>
              {t("Get your experiment materials ready")}
            </Text>
            {(Array.isArray(materials) ? materials : []).map((material, index) => (
              <Text key={index} style={{ marginTop: 10, color: palette.text,
                fontSize: 15, lineHeight: 23 }}>• {t(material)}</Text>
            ))}
            <Text style={{ marginTop: 14, color: palette.muted, fontSize: 13 }}>
              {t("Ask an adult for help with unfamiliar tools or an unsafe workspace. Follow the mission safety instructions before testing.")}
            </Text>
          </View>
        ) : null}
        {guidedPrelude ? (
          <TouchableOpacity accessibilityRole="button" onPress={() => {
            if (activeTab === "watch") {
              videoRef.current?.pause?.();
              setActiveTab("read");
            } else if (activeTab === "read" && Array.isArray(materials) && materials.length) {
              setActiveTab("ready");
            } else if (activeTab === "ready") {
              setActiveTab("read");
            } else watchStory();
          }} style={{ alignSelf: "flex-start", marginTop: 14, paddingVertical: 13,
            paddingHorizontal: 20, borderRadius: 10, backgroundColor: palette.accent }}>
            <Text style={{ color: "#ffffff", fontWeight: "900", fontSize: 15 }}>
              {activeTab === "watch" ? t("Continue to the mission briefing →")
                : activeTab === "read" && Array.isArray(materials) && materials.length
                  ? t("Get your materials ready →")
                  : activeTab === "ready" ? t("← Review mission briefing")
                  : t("← Watch the story again")}
            </Text>
          </TouchableOpacity>
        ) : null}
        {guidedPrelude && activeTab === "read" && Array.isArray(materials) && materials.length ? (
          <TouchableOpacity accessibilityRole="button" onPress={watchStory}
            style={{ alignSelf: "flex-start", marginTop: 8, padding: 10 }}>
            <Text style={{ color: palette.accent, fontWeight: "800", fontSize: 14 }}>
              {t("← Watch the story again")}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}
